import { gql } from '@urql/core'

// -----------------------------------------------------------------------------
// Fragments
// -----------------------------------------------------------------------------

export const cmsErrorFragment = gql`
  fragment CmsErrorFragment on CmsError {
    message
  }
`

// -----------------------------------------------------------------------------
// Queries
// -----------------------------------------------------------------------------

export const CurrentUserAndEnrolment = gql`
  query currentUserAndEnrolment {
    currentUser {
      id
      displayName
      chosenName
      currency {
        code
      }
    }
    currentEnrolment {
      id
      categoryType
      canAccessProgramme
      hasSiteWhichAcceptsReferralRequests
      licensorPrefersSelfFunding
      hasSiteWhichAcceptsSelfReferrals
      meta {
        nodes {
          id
          key
          value
        }
      }
      insomniaAssessment {
        id
        started
      }
      projections {
        sleepReport {
          result {
            variation
          }
        }
        weeklyAverageSleepEfficiencies {
          index
          score
          isProjected
        }
      }
      referralRequests {
        nodes {
          id
          createdAt
          updatedAt
          status
        }
      }
      upgradeProducts {
        id
        title
        key
        prices {
          value
          currency {
            code
            symbol
          }
        }
      }
    }
  }
`

// eslint-disable-next-line import/prefer-default-export
export const RoutingQuery = gql`
  query routing {
    routing {
      newReferralRequestPath
      assessmentPath
      preAssessmentPath
      chatMessagesPath
      logoutPath
      homePath
    }
  }
`

export const CurrentSleepReportQuery = gql`
  query currentSleepReport($enrolmentId: ID) {
    currentSleepReport(enrolmentId: $enrolmentId) {
      id
      seenByEnrolment
      enrolment {
        id
        canAccessTherapy
        user {
          id
          displayName
        }
      }
      result {
        title
        variation
        action
        actionText
        forceAccepted
      }
      considerations {
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          cursor
          node {
            title
            description
          }
        }
      }
      sleepEfficiencyScale {
        title
        description
        score
        total
        boundaries {
          edges {
            node {
              value
            }
          }
        }
      }
      anxietyScale {
        title
        description
        score
        total
        boundaries {
          edges {
            node {
              value
            }
          }
        }
      }
      depressionScale {
        title
        description
        score
        total
        boundaries {
          edges {
            node {
              value
            }
          }
        }
      }
      stressScale {
        title
        description
        score
        total
        boundaries {
          edges {
            node {
              value
            }
          }
        }
      }
      sleepPattern {
        averageSleepPerNightWithSleep
        hasNightsWithoutTimeAsleep
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          cursor
          node {
            id
            sleepEfficiencyScore
            date
            intoBed
            outOfBed
            sleptWell
            fellAsleep
            wokeUp
            nap
            exercise
            sleepingPills
            timeSpentOutside
            totalSleepTime
            gotNoSleep
            fragmentedSections {
              pageInfo {
                endCursor
                hasNextPage
              }
              edges {
                cursor
                node {
                  start
                  end
                }
              }
            }
            gaps {
              pageInfo {
                endCursor
                hasNextPage
              }
              edges {
                cursor
                node {
                  start
                  end
                  displayLength
                }
              }
            }
          }
        }
      }
    }
  }
`

export const ResourceCategoriesQuery = gql`
  query resourceCategories {
    resourceCategories {
      nodes {
        name
      }
    }
  }
`

export const ResourceBySlugQuery = gql`
  query resourceBySlug($resourceSlug: ID!) {
    resourceBySlug(resourceSlug: $resourceSlug) {
      __typename
      ... on Resource {
        title
        authorName
        type
        id
        description
        transcription {
          ... on Transcription {
            timecode
            body
          }
          ... on CmsError {
            ...CmsErrorFragment
          }
        }
        videoId
        videoProvider
        contentUrl
      }
      ... on CmsError {
        ...CmsErrorFragment
      }
    }
  }
  ${cmsErrorFragment}
`

// There is a dynamic resourcesByCategoryName query in src/view/utils/resources.js

export const CustomerNavigationItemsQuery = gql`
  query customerNavigationItems {
    customerNavigationItems {
      id
      key
      iconKey
      linkText
      linkUrl
      menus
      showBadge
    }
  }
`

export const ReferralRequestsQuery = gql`
  query ReferralRequests {
    ReferralRequests {
      id
      status
      created_at
      updated_at
    }
  }
`

export const QuestionnaireQuery = gql`
  query Questionnaire($key: String!) {
    questionnaire(key: $key) {
      firstStepId
      id
      key
      name
      versionNumber
      steps {
        nodes {
          category
          defaultNextStepId
          id
          contents {
            nodes {
              id
              key
              value
              valueType
            }
          }
          options {
            nodes {
              id
              key
              score
              value
            }
          }
        }
      }
    }
  }
`

// -----------------------------------------------------------------------------
// Mutations
// -----------------------------------------------------------------------------

export const CreateLogEntryMutation = gql`
  mutation createLogEntry(
    $enrolmentId: ID!
    $action: LogEntryAction!
    $description: String!
    $datetime: String!
  ) {
    createLogEntry(
      params: {
        enrolmentId: $enrolmentId
        action: $action
        description: $description
        datetime: $datetime
      }
    ) {
      enrolmentId
      whodunnitUserId
      action
      description
      datetime
    }
  }
`

export const UpdateEnrolmentInterestsMutation = gql`
  mutation updateEnrolmentInterests(
    $params: UpdateEnrolmentInterestsInputObject!
  ) {
    updateEnrolmentInterests(params: $params) {
      success
      userErrors {
        message
        type
      }
    }
  }
`

export const CreateEnrolmentMetaMutation = gql`
  mutation createEnrolmentMeta($params: CreateEnrolmentMetaInputObject!) {
    createEnrolmentMeta(params: $params) {
      userErrors {
        message
        type
      }
    }
  }
`

export const CreateInsomniaTherapyFeedbackMutationForWrittenTestimonialMutation = gql`
  mutation createInsomniaTherapyFeedback(
    $enrolmentId: ID!
    $comments: String!
    $authorName: String
    $authorLocation: String
    $authorJobRole: String
    $happyToShareAge: Boolean
    $isTestimonial: Boolean
  ) {
    createInsomniaTherapyFeedback(
      enrolmentId: $enrolmentId
      comments: $comments
      authorName: $authorName
      authorLocation: $authorLocation
      authorJobRole: $authorJobRole
      happyToShareAge: $happyToShareAge
      isTestimonial: $isTestimonial
    ) {
      id
    }
  }
`

export const CreateInsomniaTherapyFeedbackMutationForEndOfTherapyMutation = gql`
  mutation createInsomniaTherapyFeedback(
    $enrolmentId: ID!
    $comments: String!
    $score: Int!
  ) {
    createInsomniaTherapyFeedback(
      enrolmentId: $enrolmentId
      comments: $comments
      score: $score
    ) {
      id
      score
    }
  }
`

export const FeedbackContentConsentMutation = gql`
  mutation feedbackContactConsent($enrolmentId: ID!, $statement: String!) {
    feedbackContactConsent(enrolmentId: $enrolmentId, statement: $statement)
  }
`

export const MarkReportSeenMutation = gql`
  mutation recordReportSeen($enrolmentId: ID!, $id: ID!) {
    recordReportSeen(enrolmentId: $enrolmentId, id: $id) {
      id
    }
  }
`

export const CreateQuestionnaireResponse = gql`
  mutation CreateQuestionnaireResponse(
    $params: CreateQuestionnaireResponseInputObject!
  ) {
    createQuestionnaireResponse(params: $params) {
      questionnaireResult {
        id
        score
      }
      userErrors {
        message
        type
      }
    }
  }
`

export const CreateInsomniaAssessmentIntent = gql`
  mutation CreateInsomniaAssessmentIntent(
    $params: CreateInsomniaAssessmentIntentInputObject!
  ) {
    createInsomniaAssessmentIntent(params: $params) {
      nextPageUrl
      userErrors {
        message
        type
      }
    }
  }
`
